import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { GetNotificationsResponse } from './useGetNotifications';

interface MarkAnnouncementsReadInput {
    /** When omitted, all announcements are marked read. */
    announcementIds?: string[];
}

const markAnnouncementsRead = async (data: MarkAnnouncementsReadInput, sessionId: string | null) => {
    return axios.patch(`${API_URL}/announcements/read`, data, {
        headers: { Authorization: sessionId },
    });
};

export const useMarkAnnouncementsRead = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: MarkAnnouncementsReadInput = {}) =>
            markAnnouncementsRead(data, sessionContext?.sessionId ?? null),
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ['/notifications'] });
            // Optimistically drop dismissed announcements from every cached
            // notifications query so the banner clears immediately.
            queryClient.setQueriesData<GetNotificationsResponse>(
                { queryKey: ['/notifications'] },
                (old) => {
                    if (!old?.announcements) return old;
                    const remaining = variables?.announcementIds?.length
                        ? old.announcements.filter((a) => !variables.announcementIds!.includes(a._id))
                        : [];
                    return { ...old, announcements: remaining };
                }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['/notifications'] });
        },
    });
};
